<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractProtocolHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    ...userComponentProps,
    protocol: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    handoverTypes: {
      moving_in: $fn.value.pgettext('HandoverType', 'Moving-in protocol'),
      moving_out: $fn.value.pgettext('HandoverType', 'Moving-out protocol'),
    },
    newTransaction: $fn.value.pgettext('ContractProtocol', 'New protocol'),
    editTransaction: $fn.value.pgettext('ContractProtocol', 'Edit protocol'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isNew = computed(() => {
    return props.protocol.isNew;
  });
  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.handoverTypes[props.protocol.handover_type];
    }
    return null;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return isNew.value
        ? messages.value.newTransaction
        : messages.value.editTransaction;
    }
    return null;
  });

  watch(
    () => props.protocol,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('protocol');
    }
  );

  const act_1040167445267876_emit_p8hg00gzy_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4942415580156499_emit_9un6mojtg_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4942415580156499_request_px5ymwn87_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.protocol.links.self,
      method: 'patch',
      data: {
        etag: props.protocol.etag,
        locked: true,
      },
    };
  };
  const act_49312645155713_emit_ag8h3fgh5_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_49312645155713_request_kvbw05ggn_getActionArgs = async ($event) => {
    return {
      url: props.protocol.links.self,
      method: 'patch',
      data: {
        etag: props.protocol.etag,
        locked: false,
      },
    };
  };
  const act_237785848579802_emit_ya3og0ibg_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_1040167445267876_emit_p8hg00gzy = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_p8hg00gzy_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_1040167445267876_clickPreventStop_2sd2u7owi =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_p8hg00gzy,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_1040167445267876_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_clickPreventStop_2sd2u7owi],
      event: 'click.prevent.stop',
    });
  const act_4942415580156499_emit_9un6mojtg = new actionTypes.EmitAction({
    actionArgs: act_4942415580156499_emit_9un6mojtg_getActionArgs,
  });
  const inst_evh_4942415580156499_success_tgaqcceav =
    new actionTypes.EventHandlerImpl({
      action: act_4942415580156499_emit_9un6mojtg,
      event: 'success',
      displayName: 'emit',
    });
  const act_4942415580156499_request_px5ymwn87 = new actionTypes.RequestAction({
    actionArgs: act_4942415580156499_request_px5ymwn87_getActionArgs,
    displayName: 'lock',
    events: [inst_evh_4942415580156499_success_tgaqcceav],
  });
  const inst_evh_4942415580156499_clickPreventStop_1ala8i4h8 =
    new actionTypes.EventHandlerImpl({
      action: act_4942415580156499_request_px5ymwn87,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_4942415580156499_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4942415580156499_clickPreventStop_1ala8i4h8],
      event: 'click.prevent.stop',
    });
  const act_49312645155713_emit_ag8h3fgh5 = new actionTypes.EmitAction({
    actionArgs: act_49312645155713_emit_ag8h3fgh5_getActionArgs,
  });
  const inst_evh_49312645155713_success_f1axfqiag =
    new actionTypes.EventHandlerImpl({
      action: act_49312645155713_emit_ag8h3fgh5,
      event: 'success',
      displayName: 'emit',
    });
  const act_49312645155713_request_kvbw05ggn = new actionTypes.RequestAction({
    actionArgs: act_49312645155713_request_kvbw05ggn_getActionArgs,
    displayName: 'lock',
    events: [inst_evh_49312645155713_success_f1axfqiag],
  });
  const inst_evh_49312645155713_clickPreventStop_0guf4zmq4 =
    new actionTypes.EventHandlerImpl({
      action: act_49312645155713_request_kvbw05ggn,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_49312645155713_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_49312645155713_clickPreventStop_0guf4zmq4],
      event: 'click.prevent.stop',
    });
  const act_237785848579802_emit_ya3og0ibg = new actionTypes.EmitAction({
    actionArgs: act_237785848579802_emit_ya3og0ibg_getActionArgs,
  });
  const inst_evh_237785848579802_success_59c7d7d63 =
    new actionTypes.EventHandlerImpl({
      action: act_237785848579802_emit_ya3og0ibg,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_237785848579802_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_237785848579802_success_59c7d7d63],
    event: 'success',
  });

  const actions = {
    inst_evh_1040167445267876_clickPreventStop_2sd2u7owi,
    reload: inst_evh_1040167445267876_clickPreventStop_2sd2u7owi,
    inst_evh_1040167445267876_clickPreventStop,
    inst_evh_4942415580156499_success_tgaqcceav,
    inst_evh_4942415580156499_clickPreventStop_1ala8i4h8,
    lock: inst_evh_4942415580156499_clickPreventStop_1ala8i4h8,
    inst_evh_4942415580156499_clickPreventStop,
    inst_evh_49312645155713_success_f1axfqiag,
    inst_evh_49312645155713_clickPreventStop_0guf4zmq4,
    inst_evh_49312645155713_clickPreventStop,
    inst_evh_237785848579802_success_59c7d7d63,
    inst_evh_237785848579802_success,
  };

  const evh_1040167445267876_clickPreventStop_2sd2u7owi = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop_2sd2u7owi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4942415580156499_success_tgaqcceav = async (event, scope) => {
    return await actions.inst_evh_4942415580156499_success_tgaqcceav.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4942415580156499_clickPreventStop_1ala8i4h8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4942415580156499_clickPreventStop_1ala8i4h8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4942415580156499_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4942415580156499_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_49312645155713_success_f1axfqiag = async (event, scope) => {
    return await actions.inst_evh_49312645155713_success_f1axfqiag.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_49312645155713_clickPreventStop_0guf4zmq4 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_49312645155713_clickPreventStop_0guf4zmq4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_49312645155713_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_49312645155713_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_237785848579802_success_59c7d7d63 = async (event, scope) => {
    return await actions.inst_evh_237785848579802_success_59c7d7d63.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_237785848579802_success = async (event, scope) => {
    return await actions.inst_evh_237785848579802_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpBox>
        <ItpText muted class="mb-0">
          {{ resourceTitle }}
        </ItpText>
      </ItpBox>
      <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!isNew"
        @click.prevent.stop="evh_1040167445267876_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        variant="light"
        icon="icon-park-outline:preview-open"
        :href="protocol.preview_url"
        :tooltip="pgettext('ContractProtocol', 'Open preview')"
        target="_blank"
        use-auth
        show-spinner
        v-if="protocol.preview_url"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Lock')"
          :tooltip="pgettext('BtnLockTooltip', 'Lock')"
          variant="default"
          v-if="!protocol.isNew && protocol.meta.can_lock"
          @click.prevent.stop="
            evh_4942415580156499_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Unlock')"
          :tooltip="pgettext('BtnLockTooltip', 'Unlock')"
          variant="default"
          v-if="!protocol.isNew && protocol.meta.can_unlock"
          @click.prevent.stop="evh_49312645155713_clickPreventStop($event, {})"
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="protocol"
          :tooltip="pgettext('Payment', 'Delete protocol')"
          :disabled="protocol.isNew || protocol.protocol_status != 'draft'"
          v-if="!isNew"
          @success="evh_237785848579802_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </div>
  </ItpBox>
</template>
